// Pricing migration 32 (ADO-3985), T13: the lines of a multi-buy deal as the deal view lists
// them, with each product's name and image, its stock and what the cart holds of it.
//
// - The deal read names its products by SKU only. Each one's name and image come from the shared
//   product detail store, one read per product it does not hold yet (a known limit: no batch read
//   by SKU exists; one could come later). A product the catalogue cannot name is left out, never
//   shown with a made-up name.
// - Stock comes from the availability lookup at the session depot (amended AC2: the deal read
//   filters by the entry's range only, so stock never drops a product). A product the app blocks
//   (`UNAVAILABLE`, as the card does) is the out-of-stock line of R6 07u/05u: last in the list,
//   muted, with an "Unavailable" tag in place of its stepper, and not counted in the lines.
// - The free product (D-15) is named the same way; with no name it shows nothing.

import { useEffect } from 'react';
import type { MultibuyDeal } from '../api/multibuy-deals.client';
import { useAvailability } from '../availability/useAvailability';
import { useAvailabilityStore } from '../availability/useAvailabilityStore';
import { useBasketStore } from '../basket/useBasketStore';
import { useProductDetailStore } from '../product-detail/useProductDetailStore';

export interface DealLine {
  readonly skuCode: string;
  readonly name: string;
  readonly imageUrl: string | null;
  /** Cases of it in the cart now. */
  readonly quantity: number;
  readonly unavailable: boolean;
  /** It is also the deal's free product ("Also the free product", 06). */
  readonly alsoFree: boolean;
}

export interface DealFreeProduct {
  readonly skuCode: string;
  readonly name: string;
  readonly imageUrl: string | null;
}

export interface DealLines {
  /** Available lines first, in the deal's order, then the unavailable ones. */
  readonly lines: readonly DealLine[];
  /** The deal's products not out of stock (R6: the others are not counted). */
  readonly availableLines: number;
  readonly freeProduct: DealFreeProduct | null;
  /** Still naming at least one product. */
  readonly loading: boolean;
}

const NO_DEAL: DealLines = { lines: [], availableLines: 0, freeProduct: null, loading: false };

export function useDealLines(deal: MultibuyDeal | null): DealLines {
  const skus = deal === null ? [] : deal.products.map((product) => product.skuCode);
  const freeSku = deal?.freeProduct?.skuCode ?? null;
  const named = freeSku === null ? skus : [...skus, freeSku];
  const namedKey = [...new Set(named)].sort().join(',');

  useAvailability(skus);
  useEffect(() => {
    if (namedKey === '') return;
    const { load } = useProductDetailStore.getState();
    for (const sku of namedKey.split(',')) void load(sku);
  }, [namedKey]);

  const bySku = useProductDetailStore((s) => s.bySku);
  const availability = useAvailabilityStore((s) => s.entries);
  const quantities = useBasketStore((s) => s.quantityBySku);
  if (deal === null) return NO_DEAL;

  const lines = deal.products.flatMap((product): DealLine[] => {
    const summary = bySku[product.skuCode]?.product ?? null;
    if (summary === null) return [];
    return [
      {
        skuCode: product.skuCode,
        name: summary.name,
        imageUrl: summary.imageUrl,
        quantity: quantities.get(product.skuCode) ?? 0,
        unavailable: availability[product.skuCode]?.status === 'UNAVAILABLE',
        alsoFree: product.skuCode === freeSku,
      },
    ];
  });
  const ordered = [
    ...lines.filter((line) => !line.unavailable),
    ...lines.filter((line) => line.unavailable),
  ];
  const freeSummary = freeSku === null ? null : (bySku[freeSku]?.product ?? null);
  return {
    lines: ordered,
    // Counted from stock alone, so a product still being named, or one the catalogue cannot
    // name, is never taken for an out-of-stock line.
    availableLines: deal.products.filter(
      (product) => availability[product.skuCode]?.status !== 'UNAVAILABLE',
    ).length,
    freeProduct:
      freeSku === null || freeSummary === null
        ? null
        : { skuCode: freeSku, name: freeSummary.name, imageUrl: freeSummary.imageUrl },
    loading: named.some((sku) => {
      const phase = bySku[sku]?.phase;
      return phase === undefined || phase === 'loading';
    }),
  };
}
